import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { setTenantSubdomain } from '@pp/shared/platform/tenant';
import { lookupTenantsByEmail, type TenantInfo } from '../../api/authApi';

interface TenantNotFoundPageProps {
  error: string | null;
}

/**
 * Page shown when no tenant can be determined.
 * Allows users to enter their email to find their organization.
 */
export function TenantNotFoundPage({ error }: TenantNotFoundPageProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[] | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setLookupError(null);

    try {
      const result = await lookupTenantsByEmail(email.trim());

      if (result.tenants.length === 0) {
        setLookupError('No account found with that email address.');
      } else if (result.tenants.length === 1) {
        // Single tenant - redirect directly
        redirectToTenant(result.tenants[0].subdomain);
      } else {
        // Multiple tenants - show selection
        setTenants(result.tenants);
      }
    } catch {
      setLookupError('Unable to look up your account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const redirectToTenant = (subdomain: string) => {
    setTenantSubdomain(subdomain);
    // Native shell can't navigate the WebView to a different origin — assigning
    // window.location.href to https://*.packetprospector.com kicks the user out
    // to Safari (the URL is cross-origin to capacitor://localhost). Stay in app
    // and route to /login; subsequent API calls carry X-Tenant-Subdomain from
    // localStorage so the backend resolves the tenant the same way it would
    // from the hostname on web.
    if (Capacitor.isNativePlatform()) {
      navigate('/login', { replace: true });
      return;
    }
    // Web: redirect cross-subdomain so the tenant-branded login page renders
    // with the right hostname (matters for cookie domain and OAuth callbacks).
    window.location.href = `https://${subdomain}.packetprospector.com/login`;
  };

  const handleTenantSelect = (tenant: TenantInfo) => {
    redirectToTenant(tenant.subdomain);
  };

  // Show tenant selection if multiple found
  if (tenants && tenants.length > 1) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              Select Your Organization
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              Your email is associated with multiple organizations
            </p>

            <div className="space-y-3">
              {tenants.map((tenant) => (
                <button
                  key={tenant.subdomain}
                  onClick={() => handleTenantSelect(tenant)}
                  className="w-full p-4 text-left bg-gray-50 dark:bg-gray-700 hover:bg-gray-100
                           dark:hover:bg-gray-600 rounded-lg transition-colors border
                           border-gray-200 dark:border-gray-600"
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {tenant.companyName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {tenant.subdomain}.packetprospector.com
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setTenants(null)}
              className="w-full mt-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900
                       dark:hover:text-white text-sm"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Sign In
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Enter your email to find your organization
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {(lookupError || error) && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {lookupError || error}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!email.trim() || isLoading}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400
                       text-white font-medium rounded-lg transition-colors
                       focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 outline-none
                       flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Looking up...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{' '}
              <a
                href="https://www.packetprospector.com"
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Learn more
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
